import { Fragment } from 'react';
import { cn } from '@/lib/cn';

interface Section {
  id: string;
  name: string;
  display_order: number;
  row_count: number;
  seat_count_per_row: number;
  pricing_cents: number;
}

interface Seat {
  id: string;
  section_id: string;
  row_label: string;
  seat_number: number;
  blocked: boolean;
}

interface SeatMapRendererProps {
  sections: Section[];
  seats: Seat[];
  // For Sprint 8 the renderer goes interactive. For Sprint 4 it's read-only.
  soldSeatIds?: ReadonlySet<string>;
  selectedSeatIds?: ReadonlySet<string>;
  onSelectSeat?: (seatId: string) => void;
}

export function SeatMapRenderer({
  sections,
  seats,
  soldSeatIds,
  selectedSeatIds,
  onSelectSeat,
}: SeatMapRendererProps) {
  const seatsBySection = new Map<string, Seat[]>();
  for (const s of seats) {
    const list = seatsBySection.get(s.section_id) ?? [];
    list.push(s);
    seatsBySection.set(s.section_id, list);
  }

  const sortedSections = [...sections].sort((a, b) => a.display_order - b.display_order);

  return (
    <div className="space-y-8">
      {/* Stage indicator — anchors the audience perspective. */}
      <div
        aria-hidden="true"
        className="mx-auto h-3 max-w-2xl rounded-[var(--radius-full)] bg-[var(--color-ink-muted)] text-center tracking-[var(--tracking-wide)] text-[var(--color-ink-on-accent)] text-[var(--text-xs)] uppercase"
      >
        <span className="sr-only">Stage</span>
      </div>
      <p
        className="text-center tracking-[var(--tracking-wide)] text-[var(--color-ink-subtle)] text-[var(--text-xs)] uppercase"
        aria-hidden="true"
      >
        ↑ stage
      </p>

      {sortedSections.map((section) => {
        const sectionSeats = seatsBySection.get(section.id) ?? [];
        // Group by row label.
        const byRow = new Map<string, Seat[]>();
        for (const s of sectionSeats) {
          const list = byRow.get(s.row_label) ?? [];
          list.push(s);
          byRow.set(s.row_label, list);
        }
        const rowLabels = [...byRow.keys()].sort();

        return (
          <section
            key={section.id}
            aria-labelledby={`section-${section.id}`}
            className="surface-elevated p-5"
          >
            <header className="mb-3 flex items-baseline justify-between gap-3">
              <h3
                id={`section-${section.id}`}
                className="font-[var(--font-display)] text-[var(--text-lg)]"
              >
                {section.name}
              </h3>
              <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)] tabular-nums">
                ${(section.pricing_cents / 100).toFixed(2)} each
              </p>
            </header>

            {rowLabels.length === 0 ? (
              <p className="text-[var(--color-ink-muted)]">No seats configured for this section.</p>
            ) : (
              <div className="grid gap-1.5">
                {rowLabels.map((row) => {
                  const rowSeats = (byRow.get(row) ?? []).sort(
                    (a, b) => a.seat_number - b.seat_number,
                  );
                  return (
                    <div
                      key={row}
                      className="flex items-center gap-2"
                      role="group"
                      aria-label={`Row ${row}`}
                    >
                      <span className="w-6 text-right text-[var(--color-ink-subtle)] text-[var(--text-xs)] tabular-nums">
                        {row}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {rowSeats.map((seat) => (
                          <SeatSquare
                            key={seat.id}
                            seat={seat}
                            sold={soldSeatIds?.has(seat.id) ?? false}
                            selected={selectedSeatIds?.has(seat.id) ?? false}
                            onClick={onSelectSeat ? () => onSelectSeat(seat.id) : undefined}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}

      <Fragment>
        <p
          aria-hidden="true"
          className="text-center text-[var(--color-ink-subtle)] text-[var(--text-xs)]"
        >
          house →
        </p>
      </Fragment>
    </div>
  );
}

function SeatSquare({
  seat,
  sold,
  selected,
  onClick,
}: {
  seat: Seat;
  sold: boolean;
  selected: boolean;
  onClick?: (() => void) | undefined;
}) {
  const state = seat.blocked ? 'blocked' : sold ? 'sold' : selected ? 'selected' : 'available';
  const label = `Row ${seat.row_label}, seat ${seat.seat_number}, ${state}`;
  const className = cn(
    'inline-flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-xs)] tabular-nums',
    'border transition-colors duration-[var(--duration-fast)]',
    state === 'blocked' &&
      'cursor-not-allowed border-transparent bg-[var(--color-surface-sunken)] text-[var(--color-ink-subtle)] line-through',
    state === 'sold' &&
      'cursor-not-allowed border-transparent bg-[var(--color-ink-muted)] text-[var(--color-ink-on-accent)]',
    state === 'selected' &&
      'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-ink-on-accent)]',
    state === 'available' &&
      'border-[var(--color-border)] bg-[var(--color-surface)] hover:bg-[var(--color-accent-muted)]',
  );

  if (!onClick || state === 'blocked' || state === 'sold') {
    return (
      <span className={className} aria-label={label}>
        {seat.seat_number}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={state === 'selected'}
      aria-label={label}
      className={cn(className, 'cursor-pointer focus-visible:outline-none')}
    >
      {seat.seat_number}
    </button>
  );
}
